import { useContext, useEffect, useReducer } from 'react'
import {
    TextField,
    Button,
    IconButton,
    Tooltip,
    Tab,
    Tabs,
    FormControlLabel,
    Switch,
} from '@mui/material'
import {
    FormSection,
    UniversalReactSorter,
    SaveButton,
    Autocomplete,
    Grid,
} from '../../../UniversalComponents'
import { Add as AddIcon, DeleteForever } from '@mui/icons-material'
import {
    Authentication,
    usePermissions,
} from '../../../../context/AuthenticationContext'
import {
    postSignalProfile,
    getSignals,
    getEvents,
    getSignalList,
} from '../../../../apiRoutes'
import {
    permissionsCheck,
    basicValidator,
} from '../../../../utilities/functionTools'
import responseError from '../../../../context/responseError/responseError'
import SignalProfileCards from './SignalProfileCards'
import SignalProfileEventCard from './SignalProfileEventCard'
import SignalCategoryTab from './SignalCategoryTab'
import { useToastMessage } from '../../../../context/ToastPopUpContext'
interface SignalProfileManagerFormProps {
    row?: {
        id?: string
        boatInsights?: any
        electric?: boolean
        webSignals?: any
        mobileSignals?: any
        profileName?: string
        updatedBy?: string
        manufacturer?: {
            manufacturerName?: string
        }
        boatEvents?: any
        fuel?: {
            _id?: string
            signalFriendlyName?: string
        }
        battery?: {
            _id: string
            signalFriendlyName: string
        }
        gpsMode?: {
            _id: string
            signalFriendlyName: string
        }
        gpsLat?: {
            _id: string
            signalFriendlyName: string
        }
        gpsLong?: {
            _id: string
            signalFriendlyName: string
        }
        boatState?: {
            _id: string
            signalFriendlyName: string
        }
        engineTemp?: {
            _id: string
            signalFriendlyName: string
        }
    }
    formDescription?: string
    onClose: (tableRow: any, newData: any) => void
    saveCache: (e: any) => void
    permissionCheck: boolean
    quickStart?: boolean
}

const SignalProfileManagerForm = ({
    row,
    formDescription,
    onClose,
    saveCache,
    permissionCheck,
    quickStart,
}: SignalProfileManagerFormProps) => {
    const setToastMessage = useToastMessage()
    const userPermissions = usePermissions()
    const currentUser = useContext(Authentication)
    const { username } = currentUser.auth
    const tabProps = (index: number, category: string) => {
        return {
            id: `tab-${category}-${index}`,
            key: `tab-${category}-${index}`,
            'aria-controls': `tabpanel-${category}-${index}`,
            icon: (
                <Tooltip title="Remove Category">
                    <DeleteForever
                        onClick={() =>
                            handleRemoveCategoryWarning(index, category)
                        }
                    />
                </Tooltip>
            ),
        }
    }
    const defaultState = {
        formData: {
            id: row?.id || '',
            boatInsights: row?.boatInsights || [],
            webSignals: row?.webSignals || [],
            mobileSignals: row?.mobileSignals || [],
            profileName: row?.profileName || '',
            updatedBy: row?.updatedBy || username,
            manufacturer: row?.manufacturer?.manufacturerName || '',
            boatEvents: row?.boatEvents || [],
            fuel: row?.fuel?.signalFriendlyName || null,
            battery: row?.battery?.signalFriendlyName || null,
            gpsMode: row?.gpsMode?.signalFriendlyName || null,
            gpsLong: row?.gpsLong?.signalFriendlyName || null,
            gpsLat: row?.gpsLat?.signalFriendlyName || null,
            boatState: row?.boatState?.signalFriendlyName || null,
            engineTemp: row?.engineTemp?.signalFriendlyName || null,
            electric: row?.electric || false,
        },
        formValidation: {
            profileNameValidation: false,
            manufacturerValidation: false,
            isFormDirty: false,
            isFormValid: false,
        },
        boatInsightCategory: 0,
        boatInsightCategories:
            row?.boatInsights && row?.boatInsights?.length !== 0
                ? row?.boatInsights?.map(({ category }, index) => (
                      <Tab
                          label={category}
                          iconPosition="start"
                          {...tabProps(index, category)}
                      />
                  ))
                : [],
        newCategory: '',
        signals: [],
        defaultWebSignalList: [],
        defaultMobileSignalList: [],
        webSignalList: [],
        mobileSignalList: [],
        rawSignalList: [],
        filteredRawSignalList: [],
        selectedRawSignal: '',
        selectedWebSignal: '',
        selectedMobileSignal: '',
        signalEvents: [],
        signalEventList: [],
        selectedSignalEvent: '',
        defaultSignalEventList: [],
        permissionsCheck: permissionCheck,
        coreSignalList: [],
        deleteCategory: { index: 0, category: '' },
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        formData,
        formValidation,
        boatInsightCategory,
        boatInsightCategories,
        newCategory,
        signals,
        signalEvents,
        signalEventList,
        defaultSignalEventList,
        selectedSignalEvent,
        defaultWebSignalList,
        defaultMobileSignalList,
        webSignalList,
        mobileSignalList,
        rawSignalList,
        filteredRawSignalList,
        selectedRawSignal,
        selectedWebSignal,
        selectedMobileSignal,
        coreSignalList,
        deleteCategory,
        permissionsCheck,
    } = state

    const {
        id,
        boatInsights,
        webSignals,
        mobileSignals,
        profileName,
        updatedBy,
        manufacturer,
        boatEvents,
        fuel,
        battery,
        gpsMode,
        gpsLat,
        gpsLong,
        boatState,
        engineTemp,
        electric,
    } = formData

    const {
        profileNameValidation,
        manufacturerValidation,
        isFormDirty,
        isFormValid,
    } = formValidation

    const handleChange = (e: any) => {
        const { name, value } = e.target
        if (
            name === 'selectedRawSignal' ||
            name === 'selectedWebSignal' ||
            name === 'selectedMobileSignal' ||
            name === 'selectedSignalEvent'
        ) {
            setState({
                [name]: value,
            })
        } else if (name === 'newCategory') {
            setState({ newCategory: value })
        } else {
            setState({
                formData: { ...formData, [name]: value },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
            })
        }

        return saveCache({ ...formData, [name]: value })
    }

    const handleAddRawSignal = (index) => {
        const addSignal = rawSignalList.find(
            (signal) => signal.signalName === selectedRawSignal
        )

        const newFilteredRawSignalList = filteredRawSignalList.filter(
            (signal) => signal !== selectedRawSignal
        )

        const clonedBoatInsights = boatInsights
        clonedBoatInsights[index].rawSignals.push(addSignal)
        setState({
            selectedRawSignal: '',
            filteredRawSignalList: newFilteredRawSignalList,
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
            formData: {
                ...formData,
                boatInsights: clonedBoatInsights,
            },
        })
    }

    const handleRemoveRawSignal = (
        index: number,
        rawSignals: any,
        name: string
    ) => {
        const newRawSignals = rawSignals?.filter(
            (signal) => signal.signalName !== name
        )

        boatInsights[index].rawSignals = newRawSignals

        const newFilteredRawSignalList = [name, ...filteredRawSignalList]

        setState({
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
            formData: {
                ...formData,
                boatInsights: boatInsights,
            },
            filteredRawSignalList: newFilteredRawSignalList,
        })
    }

    const handleRawSignalReorder = (index, drag) => {
        const clonedRawSignals = boatInsights
        clonedRawSignals[index].rawSignals = drag
        setState({
            formData: { ...formData, boatInsights: clonedRawSignals },
        })
    }

    const handleAddSignal = (type: string) => {
        if (type === 'webSignal') {
            const addSignal = signals.find(
                (signal) => signal.signalFriendlyName === selectedWebSignal
            )

            const filteredWebSignalList = webSignalList
                .filter((signal) => signal !== selectedWebSignal)
                ?.map((signal) => signal)

            setState({
                selectedWebSignal: '',
                webSignalList: filteredWebSignalList,
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                formData: {
                    ...formData,
                    webSignals: [...webSignals, addSignal],
                },
            })
        }

        if (type === 'mobileSignal') {
            const addSignal = signals.find(
                (signal) => signal.signalFriendlyName === selectedMobileSignal
            )

            const filteredMobileSignalList = mobileSignalList
                .filter((signal) => signal !== selectedMobileSignal)
                ?.map((signal) => signal)

            setState({
                selectedMobileSignal: '',
                mobileSignalList: filteredMobileSignalList,
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                formData: {
                    ...formData,
                    mobileSignals: [...mobileSignals, addSignal],
                },
            })
        }
        if (type === 'boatEvent') {
            const addSignal = signalEvents.find(
                (signal) => signal.notificationName === selectedSignalEvent
            )

            const filteredEventSignalList = signalEventList
                .filter(
                    (notificationName) =>
                        notificationName !== selectedSignalEvent
                )
                ?.map((notificationName) => notificationName)

            setState({
                selectedSignalEvent: '',
                signalEventList: filteredEventSignalList,
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                formData: {
                    ...formData,
                    boatEvents: [
                        ...boatEvents,
                        {
                            ...addSignal,
                            logEvents: false,
                            pushNotifications: false,
                        },
                    ],
                },
            })
        }
    }

    const handleRemoveSignal = (type: string, name: string) => {
        if (type === 'webSignal') {
            const filteredWebSignals = webSignals.filter(
                (signal) => signal.signalFriendlyName !== name
            )

            setState({
                webSignalList: [name, ...webSignalList],
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                formData: {
                    ...formData,
                    webSignals: filteredWebSignals,
                },
            })
        }

        if (type === 'mobileSignal') {
            const filteredMobileSignals = mobileSignals.filter(
                (signal) => signal.signalFriendlyName !== name
            )

            setState({
                mobileSignalList: [name, ...mobileSignalList],
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                formData: {
                    ...formData,
                    mobileSignals: filteredMobileSignals,
                },
            })
        }
        if (type === 'boatEvent') {
            const filteredBoatEvents = boatEvents.filter(
                (event) => event.notificationName !== name
            )

            setState({
                signalEventList: [name, ...signalEventList],
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                formData: {
                    ...formData,
                    boatEvents: filteredBoatEvents,
                },
            })
        }
    }

    const handleReset = () =>
        setState({
            formData: defaultState.formData,
            formValidation: defaultState.formValidation,
            webSignalList: defaultWebSignalList,
            mobileSignalList: defaultMobileSignalList,
            signalEventList: defaultSignalEventList,
        })

    const handleSubmit = async () => {
        try {
            const parsedFormData = { ...formData }
            const coreSignals = [
                'fuel',
                'battery',
                'gpsMode',
                'gpsLong',
                'gpsLat',
                'boatState',
                'engineTemp',
                'charging',
                'batteryTemp',
            ]
            coreSignals.forEach((coreSignal) => {
                parsedFormData[coreSignal] = signals.find((signal) => {
                    if (
                        parsedFormData[coreSignal] ===
                        signal?.signalFriendlyName
                    ) {
                        return signal
                    }
                })
                return parsedFormData
            })

            let res: any

            res = await postSignalProfile(parsedFormData)

            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `Profile: ${data.profileName} was ${
                            row?.id ? 'edited' : 'added'
                        } successfully`,
                        'success'
                    )

                    onClose(row?.profileName, data)
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleSetEvent = (type, index) => {
        const cloneBoatEvents = [...boatEvents]
        if (type === 'logEvents') {
            cloneBoatEvents[index].logEvents = !boatEvents[index].logEvents
        }
        if (type === 'pushNotifications') {
            cloneBoatEvents[index].pushNotifications =
                !boatEvents[index].pushNotifications
        }

        setState({
            formData: {
                ...formData,
                boatEvents: cloneBoatEvents,
            },
        })
    }

    const handleAddCategory = () => {
        if (newCategory) {
            const newFilteredRawSignalList = rawSignalList?.map(
                (signal) => signal.signalName
            )
            setState({
                formData: {
                    ...formData,
                    boatInsights: [
                        ...boatInsights,
                        { category: newCategory, rawSignals: [] },
                    ],
                },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                boatInsightCategory:
                    boatInsights.length === 0
                        ? 0
                        : boatInsightCategories.length,
                boatInsightCategories: [
                    ...boatInsightCategories,
                    <Tab
                        label={newCategory}
                        iconPosition="start"
                        {...tabProps(
                            boatInsightCategories.length - 1,
                            newCategory
                        )}
                    />,
                ],
                newCategory: '',
                filteredRawSignalList: newFilteredRawSignalList,
            })
        }
    }

    const setCurrentTab = (event: React.SyntheticEvent, newValue: number) => {
        const filterUsedSignals = rawSignalList
            .filter((signal) => {
                const findIndex = boatInsights[newValue].rawSignals.findIndex(
                    (rawsignal) => signal.signalName === rawsignal.signalName
                )
                if (findIndex === -1) {
                    return signal
                }
            })
            ?.map((signal) => signal.signalName)

        setState({
            boatInsightCategory: newValue,
            filteredRawSignalList: filterUsedSignals,
        })
    }

    const handleRemoveCategoryWarning = (index: number, category: string) => {
        setToastMessage(
            `Are you sure you want to delete ${category} category?`,
            'warning',
            () => handleRemoveCategory(index, category)
        )
    }

    const handleRemoveCategory = (index: number, category: string) => {
        setState({
            deleteCategory: { index, category },
        })
        setToastMessage(`Deleted Category: ${category}`)
    }

    const getData = async () => {
        try {
            const signalData = await getSignals(true)
            const signalEventData = await getEvents(true)
            const rawSignalData = await getSignalList()

            let rawSignalList = []
            let newFilteredRawSignalList = []
            let webSignalList = []
            let mobileSignalList = []
            let signalEventList = []
            let coreSignalList = []

            if (rawSignalData && rawSignalData.length !== 0) {
                rawSignalList = rawSignalData
                newFilteredRawSignalList = rawSignalData?.map(
                    (signal) => signal.signalName
                )

                if (boatInsights && boatInsights?.length !== 0) {
                    newFilteredRawSignalList = rawSignalData
                        .filter((signal) => {
                            const findIndex =
                                boatInsights[0].rawSignals.findIndex(
                                    (rawsignal) =>
                                        signal.signalName ===
                                        rawsignal.signalName
                                )
                            if (findIndex === -1) {
                                return signal
                            }
                        })
                        ?.map((signal) => signal.signalName)
                }
            }
            if (signalData && signalData?.length !== 0) {
                webSignalList = signalData?.map(
                    (signal) => signal?.signalFriendlyName
                )
                mobileSignalList = signalData?.map(
                    (signal) => signal?.signalFriendlyName
                )
                signalEventList = signalEventData?.map(
                    (event) => event?.notificationName
                )

                coreSignalList = signalData?.map(
                    (signal) => signal?.signalFriendlyName
                )

                if (row?.webSignals && row?.webSignals?.length !== 0) {
                    webSignals.forEach(({ signalFriendlyName }) => {
                        webSignalList = webSignalList.filter(
                            (signal) => signal !== signalFriendlyName
                        )

                        return webSignalList
                    })
                }
                if (row?.mobileSignals) {
                    mobileSignals.forEach(({ signalFriendlyName }) => {
                        mobileSignalList = mobileSignalList.filter(
                            (signal) => signal !== signalFriendlyName
                        )
                        return mobileSignalList
                    })
                }
                if (row?.boatEvents) {
                    boatEvents.forEach(({ notificationName }) => {
                        signalEventList = signalEventList.filter(
                            (signal) => signal !== notificationName
                        )
                        return signalEventList
                    })
                }

                setState({
                    signals: signalData,
                    defaultWebSignalList: webSignalList,
                    defaultMobileSignalList: mobileSignalList,
                    rawSignalList: rawSignalList,
                    filteredRawSignalList: newFilteredRawSignalList,
                    webSignalList: webSignalList,
                    mobileSignalList: mobileSignalList,
                    coreSignalList: coreSignalList,
                    signalEvents: signalEventData,
                    signalEventList,
                    defaultSignalEventList: signalEventList,
                })
            } else {
                setToastMessage(
                    'There are no signals to fetch. Please add one or check your connection.',
                    'error'
                )
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    useEffect(() => {
        getData()
    }, [])

    useEffect(() => {
        if (isFormDirty && !profileNameValidation && profileName) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [isFormDirty, profileNameValidation, profileName])

    useEffect(() => {
        if (deleteCategory.category) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
                formData: {
                    ...formData,
                    boatInsights: boatInsights?.filter(
                        (category, categoryIndex) =>
                            categoryIndex !== deleteCategory.index
                    ),
                },
                boatInsightCategory: 0,
                boatInsightCategories: boatInsightCategories.filter(
                    (category, categoryIndex) =>
                        categoryIndex !== deleteCategory.index
                ),
                deleteCategory: { index: 0, category: '' },
            })
        }
    }, [deleteCategory])

    return (
        <Grid container spacing={2}>
            <Grid container spacing={2} sx={{ marginTop: 4 }}>
                <Grid item xs={12} md={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormSection
                                title="Signal Profile Details"
                                titleAlignment="left"
                            >
                                <Grid item xs={12}>
                                    <TextField
                                        required={true}
                                        name="profileName"
                                        label="Profile Name"
                                        fullWidth
                                        value={profileName}
                                        onChange={handleChange}
                                        disabled={permissionCheck}
                                        onBlur={(e: any) =>
                                            basicValidator(
                                                e,
                                                formValidation,
                                                setState
                                            )
                                        }
                                        error={profileNameValidation}
                                        helperText={
                                            profileNameValidation
                                                ? 'Please enter a profile name.'
                                                : ''
                                        }
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                onChange={() =>
                                                    setState({
                                                        formData: {
                                                            ...formData,
                                                            electric: !electric,
                                                        },
                                                        formValidation: {
                                                            ...formValidation,
                                                            isFormDirty: true,
                                                        },
                                                    })
                                                }
                                                inputProps={{
                                                    'aria-label': 'controlled',
                                                }}
                                                id="switch.electric"
                                                name="switch.electic"
                                                checked={electric}
                                                key="switch.dealerVisibilty"
                                            />
                                        }
                                        label="Boat is Electric"
                                        key="switch.electric"
                                        disabled={permissionsCheck}
                                    />
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormSection
                                title="Core Signals"
                                titleAlignment="left"
                            >
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        name="fuel"
                                        label={
                                            !electric ? 'Fuel' : 'HV Battery'
                                        }
                                        fullWidth
                                        options={coreSignalList}
                                        value={fuel}
                                        handleChange={handleChange}
                                        disabled={permissionCheck}
                                        clearFunction={() =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    fuel: null,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        name="gpsLong"
                                        label="GPS Longitude"
                                        fullWidth
                                        options={coreSignalList}
                                        value={gpsLong}
                                        handleChange={handleChange}
                                        disabled={permissionCheck}
                                        clearFunction={() =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    gpsLong: null,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        name="boatState"
                                        label="Boat State"
                                        fullWidth
                                        options={coreSignalList}
                                        value={boatState}
                                        handleChange={handleChange}
                                        disabled={permissionCheck}
                                        clearFunction={() =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    boatState: null,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        name="gpsLat"
                                        label="GPS Latitude"
                                        fullWidth
                                        options={coreSignalList}
                                        value={gpsLat}
                                        handleChange={handleChange}
                                        disabled={permissionCheck}
                                        clearFunction={() =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    gpsLat: null,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        name="battery"
                                        label="Battery"
                                        fullWidth
                                        options={coreSignalList}
                                        value={battery}
                                        handleChange={handleChange}
                                        disabled={permissionCheck}
                                        clearFunction={() =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    battery: null,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        name="gpsMode"
                                        label="GPS Mode"
                                        fullWidth
                                        options={coreSignalList}
                                        value={gpsMode}
                                        handleChange={handleChange}
                                        disabled={permissionCheck}
                                        clearFunction={() =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    gpsMode: null,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        name="engineTemp"
                                        label={
                                            !electric
                                                ? 'Engine Temp'
                                                : 'Charge Power'
                                        }
                                        fullWidth
                                        options={coreSignalList}
                                        value={engineTemp}
                                        handleChange={handleChange}
                                        disabled={permissionCheck}
                                        clearFunction={() =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    engineTemp: null,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                            </FormSection>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <FormSection
                            title="Boat Insight Telemetry: (Drag to Re-Order)"
                            titleAlignment="left"
                        >
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sx={{ marginBottom: 5 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={8}>
                                                <Tabs
                                                    value={boatInsightCategory}
                                                    onChange={setCurrentTab}
                                                    aria-label="Category Tabs"
                                                >
                                                    {boatInsightCategories}
                                                </Tabs>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={10}>
                                                        <TextField
                                                            name="newCategory"
                                                            label="Add New Category"
                                                            fullWidth
                                                            value={newCategory}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            onKeyDown={(e) =>
                                                                e.key ===
                                                                    'Enter' &&
                                                                handleAddCategory()
                                                            }
                                                            disabled={
                                                                permissionCheck
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={2}>
                                                        <IconButton
                                                            onClick={
                                                                handleAddCategory
                                                            }
                                                            aria-label="addIcon"
                                                            disabled={
                                                                permissionCheck
                                                            }
                                                            sx={{
                                                                borderRadius: 1,
                                                                background:
                                                                    '#8cc63f',
                                                                color: 'white',

                                                                height: 55,
                                                                width: 55,
                                                            }}
                                                        >
                                                            <Tooltip title="Add new category">
                                                                <AddIcon />
                                                            </Tooltip>
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {/* {Removing container or changing into an item will cause box creep} */}
                                        <Grid
                                            container
                                            spacing={2}
                                            sx={{ marginLeft: -1 }}
                                        >
                                            {boatInsights?.map(
                                                (boatInsight, index) => (
                                                    <SignalCategoryTab
                                                        key={`Signal-Category-${index}`}
                                                        boatInsightCategory={
                                                            boatInsightCategory
                                                        }
                                                        index={index}
                                                        handleChange={
                                                            handleChange
                                                        }
                                                        rawSignals={
                                                            boatInsight.rawSignals
                                                        }
                                                        filteredRawSignalList={
                                                            filteredRawSignalList
                                                        }
                                                        handleRawSignalReorder={
                                                            handleRawSignalReorder
                                                        }
                                                        handleAddRawSignal={
                                                            handleAddRawSignal
                                                        }
                                                        handleRemoveRawSignal={
                                                            handleRemoveRawSignal
                                                        }
                                                        selectedRawSignal={
                                                            selectedRawSignal
                                                        }
                                                        disabled={
                                                            permissionCheck
                                                        }
                                                    />
                                                )
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}></Grid>
                        </FormSection>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormSection
                                title="Web Signals: (Drag to Re-Order)"
                                titleAlignment="left"
                            >
                                <Grid item xs={12} md={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={7}>
                                            <Autocomplete
                                                options={webSignalList}
                                                value={selectedWebSignal}
                                                handleChange={handleChange}
                                                required={true}
                                                label="Select Signal"
                                                name="selectedWebSignal"
                                                disabled={permissionCheck}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() =>
                                                    handleAddSignal('webSignal')
                                                }
                                                disabled={
                                                    !selectedWebSignal &&
                                                    permissionCheck
                                                }
                                                sx={{
                                                    width: '100%',
                                                    height: 55,
                                                }}
                                            >
                                                Add Signal
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <UniversalReactSorter
                                        list={webSignals}
                                        disabled={permissionCheck}
                                        setList={(drag) =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    webSignals: drag,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                        group="fixed"
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'left',
                                            flexWrap: 'wrap',
                                            marginTop: 5,
                                            width: '100%',
                                        }}
                                    >
                                        {webSignals &&
                                            webSignals?.length !== 0 &&
                                            webSignals?.map(
                                                ({
                                                    _id,
                                                    signalIconUrl,
                                                    signalFriendlyName,
                                                    unitOfMeasurement,
                                                    signalUnitOfMeasurement,
                                                }) => (
                                                    <SignalProfileCards
                                                        image={signalIconUrl}
                                                        key={`${_id}.card`}
                                                        description={'0'}
                                                        unitOfMeasurement={
                                                            unitOfMeasurement?.unitOfMeasurementAbrv ||
                                                            signalUnitOfMeasurement?.signalUOMAbrv
                                                        }
                                                        title={
                                                            signalFriendlyName
                                                        }
                                                        size="web"
                                                        type="webSignal"
                                                        disabled={
                                                            permissionCheck
                                                        }
                                                        removeSignal={
                                                            handleRemoveSignal
                                                        }
                                                    />
                                                )
                                            )}
                                    </UniversalReactSorter>
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormSection
                                title="Mobile Signals (Drag to Re-order)"
                                titleAlignment="left"
                            >
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={7}>
                                            <Autocomplete
                                                options={mobileSignalList}
                                                value={selectedMobileSignal}
                                                handleChange={handleChange}
                                                required={true}
                                                label="Select Signal"
                                                name="selectedMobileSignal"
                                                disabled={permissionCheck}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() =>
                                                    handleAddSignal(
                                                        'mobileSignal'
                                                    )
                                                }
                                                disabled={!selectedMobileSignal}
                                                sx={{
                                                    width: '100%',
                                                    height: 55,
                                                }}
                                            >
                                                Add Signal
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <UniversalReactSorter
                                        list={mobileSignals}
                                        setList={(drag) =>
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    mobileSignals: drag,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                        disabled={permissionCheck}
                                        group="fixed"
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'left',
                                            flexWrap: 'wrap',
                                            marginTop: 5,
                                            width: '100%',
                                        }}
                                    >
                                        {mobileSignals?.map(
                                            ({
                                                _id,
                                                signalIconUrl,
                                                signalFriendlyName,
                                                unitOfMeasurement,
                                                signalUnitOfMeasurement,
                                            }) => (
                                                <SignalProfileCards
                                                    image={signalIconUrl}
                                                    key={`${_id}.card`}
                                                    description={'0'}
                                                    unitOfMeasurement={
                                                        unitOfMeasurement?.unitOfMeasurementAbrv ||
                                                        signalUnitOfMeasurement?.signalUOMAbrv
                                                    }
                                                    type="mobileSignal"
                                                    title={signalFriendlyName}
                                                    removeSignal={
                                                        handleRemoveSignal
                                                    }
                                                    disabled={permissionCheck}
                                                />
                                            )
                                        )}
                                    </UniversalReactSorter>
                                </Grid>
                            </FormSection>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={12}>
                    <FormSection
                        title="Boat Signal Events"
                        titleAlignment="left"
                    >
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                    <Autocomplete
                                        options={signalEventList}
                                        value={selectedSignalEvent}
                                        handleChange={handleChange}
                                        label="Select Signal Event"
                                        name="selectedSignalEvent"
                                        disabled={permissionCheck}
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                            handleAddSignal('boatEvent')
                                        }
                                        disabled={
                                            !selectedSignalEvent &&
                                            permissionCheck
                                        }
                                        sx={{
                                            width: '100%',
                                            height: 55,
                                        }}
                                    >
                                        Add Signal
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <UniversalReactSorter
                                list={boatEvents}
                                setList={(drag) =>
                                    setState({
                                        formData: {
                                            ...formData,
                                            boatEvents: drag,
                                        },
                                        formValidation: {
                                            ...formValidation,
                                            isFormDirty: true,
                                        },
                                    })
                                }
                                group="fixed"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'left',
                                    flexWrap: 'wrap',
                                    marginTop: 5,
                                    width: '100%',
                                }}
                            >
                                {boatEvents?.map(
                                    (
                                        {
                                            _id,
                                            notificationName,
                                            logEvents,
                                            pushNotifications,
                                        },
                                        index
                                    ) => (
                                        <SignalProfileEventCard
                                            key={`${_id}.card`}
                                            logEvents={logEvents}
                                            pushNotifications={
                                                pushNotifications
                                            }
                                            notificationName={notificationName}
                                            index={index}
                                            setEvent={handleSetEvent}
                                            removeEvent={handleRemoveSignal}
                                            type="boatEvent"
                                            disabled={permissionCheck}
                                        />
                                    )
                                )}
                            </UniversalReactSorter>
                        </Grid>
                    </FormSection>
                </Grid>
            </Grid>

            <Grid xs={12} sx={{ marginTop: -2 }}>
                <SaveButton
                    handleSubmit={handleSubmit}
                    handleReset={handleReset}
                    disabledSave={!isFormValid}
                    disabledReset={!isFormDirty}
                    quickStart={quickStart}
                />
            </Grid>
        </Grid>
    )
}

export default SignalProfileManagerForm
